import { useEffect, useState } from "react";
import client, { apiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DownloadSimple, UploadSimple } from "@phosphor-icons/react";

export const fmtSize = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);
export const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export async function downloadVersion(docId, v) {
  try {
    const { data } = await client.get(`/dms/documents/${docId}/versions/${v.version}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = v.original_filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(apiError(e)); }
}

export function NewDocumentDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setForm({ title: "", description: "" }); setFile(null); } }, [open]);

  const submit = async () => {
    if (!form.title.trim() || !file) { toast.error("Title and file are required"); return; }
    const fd = new FormData();
    fd.append("title", form.title); fd.append("description", form.description); fd.append("file", file);
    setBusy(true);
    try { await client.post("/dms/documents", fd); toast.success("Document uploaded (v1)"); onOpenChange(false); onCreated(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm" data-testid="dms-new-dialog">
        <DialogHeader><DialogTitle>New Document</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div><label className="label-cap">Title</label>
            <Input data-testid="dms-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div><label className="label-cap">Description</label>
            <Textarea data-testid="dms-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div><label className="label-cap">File <span className="normal-case tracking-normal text-slate-400 text-[10px]">(max 25 MB, any type)</span></label>
            <Input data-testid="dms-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="dms-create-submit" disabled={busy} onClick={submit} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function NewVersionUpload({ docId, nextVersion, onUploaded }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { await client.post(`/dms/documents/${docId}/versions`, fd); toast.success(`Uploaded v${nextVersion}`); setFile(null); onUploaded(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <Input key={nextVersion} data-testid="dms-version-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      <Button data-testid="dms-version-upload-btn" disabled={!file || busy} onClick={submit} className="bg-orange-600 hover:bg-orange-700 rounded-sm shrink-0">
        <UploadSimple size={16} className="mr-1" /> {busy ? "Uploading…" : `Upload as v${nextVersion}`}
      </Button>
    </div>
  );
}

export function VersionList({ doc }) {
  return (
    <div className="border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="dms-version-list">
      {doc.versions.map((v) => (
        <div key={v.version} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`dms-version-row-${v.version}`}>
          <span className={`px-2 py-0.5 text-xs font-bold rounded-sm ${v.version === doc.latest_version ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>v{v.version}</span>
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium">{v.original_filename}</div>
            <div className="text-xs text-slate-500">{v.uploaded_by_name} · {fmtDate(v.uploaded_at)} · {fmtSize(v.size)}</div>
          </div>
          <button data-testid={`dms-download-v${v.version}`} onClick={() => downloadVersion(doc.id, v)} className="p-2 rounded-sm hover:bg-slate-100" title="Download">
            <DownloadSimple size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}

export function SharePanel({ doc, onSaved }) {
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState(doc.shared_with.map((s) => s.mobile));
  const [q, setQ] = useState("");
  useEffect(() => { client.get("/dms/users").then(({ data }) => setPeople(data.filter((p) => p.mobile !== doc.owner))); }, [doc.owner]);
  const toggle = (m) => setSelected((s) => (s.includes(m) ? s.filter((x) => x !== m) : [...s, m]));
  const save = async () => {
    try { await client.put(`/dms/documents/${doc.id}/share`, { mobiles: selected }); toast.success("Sharing updated"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const shown = people.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="grid gap-2" data-testid="dms-share-panel">
      <Input data-testid="dms-share-search" placeholder="Search user by name…" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-sm divide-y divide-slate-100">
        {shown.map((p) => (
          <label key={p.mobile} className="flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50" data-testid={`dms-share-user-${p.mobile}`}>
            <Checkbox checked={selected.includes(p.mobile)} onCheckedChange={() => toggle(p.mobile)} />
            <span>{p.name}</span>
          </label>
        ))}
        {shown.length === 0 && <div className="px-3 py-3 text-xs text-slate-500">No matching users</div>}
      </div>
      <Button data-testid="dms-share-save" onClick={save} variant="outline" className="rounded-sm justify-self-start">Save sharing ({selected.length})</Button>
    </div>
  );
}
